import { faCalendarDay } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ZonedDateTime } from '@js-joda/core';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import RSuiteDateRangePicker, {
  DateRange,
  DisabledDateFunction,
  RangeType,
} from 'rsuite/DateRangePicker';

import 'rsuite/dist/rsuite-no-reset.min.css';
import styled from 'styled-components';
import { BLACK2 } from '../../../theme';
import { getEndOfTime, getStartOfTime } from '../../../utils/date';
import { EventLabels } from '../../../utils/trackingEvents';
import { TextFieldCSS } from '../Input';
import Text, { TextColor, TextKind } from '../Text';
import {
  defaultPredefinedRanges,
  RangeTypeWithEventLabel,
} from './predefinedRanges';
import './rsuite-date-range-picker.css';

const StyledDateRangePicker = styled(RSuiteDateRangePicker)`
  input {
    ${TextFieldCSS}
  }
`;

const FooterNote = styled.div`
  display: flex;
  align-items: center;
`;

/**
 * The popover footer is a two-cell flex row: `placement: 'bottom'` shortcuts on
 * the left, Apply on the right. Ours all sit on the left panel instead, so the
 * first cell renders empty and the note can take it — but the component accepts
 * no footer children, so it has to be portalled in once the popover mounts.
 */
const FOOTER_SLOT = '.rs-picker-toolbar > .rs-stack-item:first-child';

export type ZdtDateRange = {
  start?: ZonedDateTime | null;
  end?: ZonedDateTime | null;
};

interface DateRangePickerProps {
  onChange: (value: ZdtDateRange, label?: EventLabels) => void;
  value: ZdtDateRange;
  onClear?: () => void;
  onOpen?: () => void;
  clearable?: boolean;
  predefinedRanges?: RangeType[];
  shouldDisableDate?: DisabledDateFunction;
  /** Rendered in the popover footer, to the left of Apply. */
  note?: React.ReactNode;
}

const CalendarIcon = () => (
  <FontAwesomeIcon icon={faCalendarDay} color={BLACK2} fontSize={16} />
);

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  onClear,
  onOpen,
  clearable,
  predefinedRanges,
  shouldDisableDate,
  note,
}) => {
  const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);
  const startJsDate = value.start
    ? new Date(value.start.toString())
    : new Date(getStartOfTime().toString());
  const endJsDate = value.end
    ? new Date(value.end.toString())
    : new Date(getEndOfTime().toString());

  const handleChange = (range: [Date, Date], label: EventLabels) => {
    onChange(
      {
        start: ZonedDateTime.parse(range[0].toISOString()),
        end: ZonedDateTime.parse(range[1].toISOString()),
      },
      label,
    );
  };

  const handleOnClean = () => {
    if (onClear) {
      onClear();
    }
  };

  return (
    <>
      <StyledDateRangePicker
        aria-label="date range picker"
        character=" - "
        onOk={(range: DateRange) => handleChange(range, EventLabels.Apply)}
        onShortcutClick={(range: RangeType) => {
          const rangeWithEventLabel = range as RangeTypeWithEventLabel;
          if (
            typeof rangeWithEventLabel.value === 'object' &&
            rangeWithEventLabel.value !== null &&
            rangeWithEventLabel.eventlabel
          ) {
            handleChange(
              rangeWithEventLabel.value,
              rangeWithEventLabel.eventlabel,
            );
          }
        }}
        locale={{
          ok: 'Apply',
        }}
        caretAs={CalendarIcon}
        cleanable={clearable}
        format="MMM dd, yyyy"
        editable={false}
        onOpen={onOpen}
        value={[startJsDate, endJsDate]}
        onClean={handleOnClean}
        ranges={predefinedRanges || defaultPredefinedRanges}
        shouldDisableDate={shouldDisableDate}
        onEnter={(popup: HTMLElement) =>
          setFooterSlot(popup.querySelector(FOOTER_SLOT))
        }
        onExited={() => setFooterSlot(null)}
      />
      {note &&
        footerSlot &&
        createPortal(
          <FooterNote>
            <Text kind={TextKind.BodyS} color={TextColor.Secondary}>
              {note}
            </Text>
          </FooterNote>,
          footerSlot,
        )}
    </>
  );
};
